import loadjs from 'loadjs';
import * as Sentry from '@sentry/nextjs';
import { CustomError, ScriptError } from '@/types/errors';
import posthog from '@/lib/posthogWrapper';

export interface ScriptOptions {
	src: string;
	success?: () => void;
	error?: () => void;
	bundleId?: string;
	mustBeFirst?: boolean;
	type?: 'module' | 'text/javascript';
	timeout?: number; // milliseconds, defaults to 10000
	retry?: boolean; // if true, will retry once on failure, defaults to false
	retryDelay?: number; // milliseconds before retry, defaults to 1000
}

// Keep track of loaded scripts and their names
const loadedScripts: Record<string, string> = {};

// Systemic failure detection
interface FailureTracker {
	timestamp: number;
	script: string;
	failureType: string;
	src: string;
}

interface SystemicFailureResult {
	isSystemic: boolean;
	failureCount: number;
	affectedScripts: string[];
	probableCause: string | null;
	confidence: 'high' | 'medium' | 'low';
}

export type SystemicFailureHandler = (result: SystemicFailureResult) => void;

const recentFailures: FailureTracker[] = [];
const FAILURE_WINDOW = 15000; // 15 seconds - Kept for test utilities backward compatibility (no longer used for filtering)
const SYSTEMIC_THRESHOLD = 2; // 2+ distinct scripts failing = systemic issue
let systemicFailureCallback: SystemicFailureHandler | null = null;
let systemicFailureDetected = false;

export const setSystemicFailureHandler = (handler: SystemicFailureHandler | null) => {
	systemicFailureCallback = handler;
};

/**
 * Check if a systemic script loading failure has been detected.
 * This indicates likely ad blocker or network issues affecting multiple scripts.
 */
export const hasSystemicFailure = (): boolean => {
	return systemicFailureDetected;
};

// Test utilities - for testing only
export const __TEST_ONLY__ = {
	clearFailures: () => {
		recentFailures.length = 0;
		systemicFailureCallback = null;
		systemicFailureDetected = false;
	},
	getFailureState: () => ({
		failures: [...recentFailures],
		threshold: SYSTEMIC_THRESHOLD,
		window: FAILURE_WINDOW,
	}),
};

const diagnoseProbableCause = (
	failures: FailureTracker[],
): { cause: string; confidence: 'high' | 'medium' | 'low' } => {
	const hasTimeouts = failures.some((f) => f.failureType === 'timeout');
	const isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;

	// HIGH CONFIDENCE DETECTIONS ONLY

	// Pattern 1: Browser says offline
	if (!isOnline) {
		return { cause: 'No Internet Connection', confidence: 'high' };
	}

	// Pattern 2: Scripts timing out (or mixed errors + timeouts) = Network issues
	// If network is bad, some scripts might timeout while others fail quickly
	if (hasTimeouts) {
		return { cause: 'Network Issues', confidence: 'high' };
	}

	// Pattern 3: All scripts errored instantly (no timeouts) = Blocking software
	// Ad blockers, firewalls, privacy extensions intercept and block requests immediately
	return { cause: 'Ad Blocker or Corporate Firewall', confidence: 'high' };
};

const trackFailure = (
	script: string,
	failureType: string,
	src: string,
): SystemicFailureResult | null => {
	// Add new failure
	recentFailures.push({ timestamp: Date.now(), script, failureType, src });

	// Get unique scripts that failed
	const uniqueScripts = new Set(recentFailures.map((f) => f.script));

	// Check if systemic (2+ distinct scripts)
	if (uniqueScripts.size >= SYSTEMIC_THRESHOLD) {
		// Mark that a systemic failure has been detected
		systemicFailureDetected = true;

		const diagnosis = diagnoseProbableCause(recentFailures);

		const result: SystemicFailureResult = {
			isSystemic: true,
			failureCount: recentFailures.length,
			affectedScripts: Array.from(uniqueScripts),
			probableCause: diagnosis.cause,
			confidence: diagnosis.confidence,
		};

		// Report to Sentry (once per detection)
		if (uniqueScripts.size === SYSTEMIC_THRESHOLD) {
			Sentry.withScope((scope) => {
				scope.setTag('errorType', 'systemic-script-failure');
				scope.setTag('probableCause', diagnosis.cause);
				scope.setTag('confidence', diagnosis.confidence);
				scope.setLevel('warning');

				scope.setContext('systemicFailure', {
					failureCount: recentFailures.length,
					uniqueScripts: Array.from(uniqueScripts),
					networkOnline: typeof navigator !== 'undefined' ? navigator.onLine : 'unknown',
					probableCause: diagnosis.cause,
					failures: recentFailures.map((f) => ({
						script: f.script,
						type: f.failureType,
						src: f.src,
					})),
				});

				Sentry.captureException(
					new CustomError(
						`Systemic script loading failure: ${Array.from(uniqueScripts).join(', ')} failed. Probable cause: ${diagnosis.cause}`,
						'Systemic Script Failure',
					),
				);
			});

			posthog.capture('systemic_script_failure', {
				failureCount: recentFailures.length,
				affectedScripts: Array.from(uniqueScripts),
				probableCause: diagnosis.cause,
				confidence: diagnosis.confidence,
				networkOnline: typeof navigator !== 'undefined' ? navigator.onLine : 'unknown',
			});
		}

		return result;
	}

	return null;
};

/**
 * Manually track a script failure for systemic detection.
 * Use this for scripts not loaded via loadScript() (e.g., VWO, Facebook Pixel).
 *
 * @param scriptName - Name of the script that failed (e.g., 'vwo', 'facebook-pixel')
 * @param failureType - Type of failure: 'network', 'timeout', 'blocked', or 'unknown'
 * @returns SystemicFailureResult if systemic failure detected, null otherwise
 */
export const trackScriptFailure = (
	scriptName: string,
	failureType: 'network' | 'timeout' | 'blocked' | 'unknown' = 'network',
): SystemicFailureResult | null => {
	return trackFailure(scriptName, failureType, '');
};

// Helper to extract filename from URL or stack trace
const getScriptName = (url: string) => {
	try {
		return new URL(url).pathname.split('/').pop() || url;
	} catch {
		return url;
	}
};

// Helper to identify third-party scripts
const isThirdPartyScript = (filename: string) => {
	try {
		// Get current domain
		const currentDomain = window.location.hostname;
		// Parse the script URL
		const scriptDomain = new URL(filename).hostname;
		// Return true if script is from a different domain
		return scriptDomain !== currentDomain;
	} catch {
		// If URL parsing fails, return false to be safe
		return false;
	}
};

// Set up global error handler
if (typeof window !== 'undefined') {
	window.addEventListener('error', (event) => {
		// Try to determine if error came from one of our loaded scripts
		const errorScript = event.filename && getScriptName(event.filename);
		const matchedScript = errorScript && loadedScripts[errorScript];

		if (matchedScript || (errorScript && isThirdPartyScript(event.filename))) {
			Sentry.withScope((scope) => {
				scope.setTag('script', matchedScript || 'third-party');
				scope.setTag('errorType', 'runtime');
				scope.setTag('scriptFile', errorScript);
				scope.setLevel('error');
				Sentry.captureException(event.error);
			});
		}
	});
}

const detectLoadFailureType = (
	src: string,
): { type: 'blocked' | 'network' | 'unknown'; finalUrl?: string } => {
	if (!src || typeof src !== 'string') {
		throw new ScriptError('Invalid source URL provided');
	}

	// iOS Safari might modify the URL (e.g., add cache busting parameters)
	// so we use a partial match for the querySelector
	const scriptElement = document.querySelector(`script[src*="${src.split('?')[0]}"]`);

	if (!scriptElement) {
		// Script element is missing from DOM, likely due to:
		// - Content Security Policy (CSP) blocking script creation
		// - Browser security policy
		// - DOM manipulation prevention
		// - iOS memory constraints
		return { type: 'blocked' };
	}

	// Get the final URL from the script element
	let finalUrl: string | undefined;
	if (scriptElement instanceof HTMLScriptElement) {
		finalUrl = scriptElement.src;
	}

	// Script element exists but failed to load. This could be due to:
	// - Ad-blocker intercepting the request
	// - Network failure
	// - 404 or other HTTP error
	// - CORS issue
	// - iOS Safari specific issues (stricter CSP, memory limits)
	// Without access to detailed network info, we can't distinguish between these
	return { type: 'network', finalUrl };
};

export const loadScript = (options: ScriptOptions, name: string, isRetry = false) => {
	// Use a different bundleId for retries to avoid loadjs internal state issues
	const baseBundleId = options.bundleId || options.src.replace(/[^\w]/g, '');
	const bundleId = isRetry ? `${baseBundleId}-retry` : baseBundleId;
	const scriptName = getScriptName(options.src);

	// Skip duplicate check on retry - we want to retry even if first attempt registered the bundleId
	if (!isRetry && loadjs.isDefined(bundleId)) {
		return;
	}

	// Register this script
	loadedScripts[scriptName] = name;

	const shouldRetry = options.retry === true;
	const retryDelay = options.retryDelay ?? 1000;
	const timeout = options.timeout ?? 10000;

	if (isRetry) {
		posthog.capture('script_load_retry', {
			script: name,
			src: options.src,
			bundleId,
		});
	} else {
		posthog.capture('script_load_started', {
			script: name,
			src: options.src,
			bundleId,
			willRetryOnFailure: shouldRetry,
		});
	}

	let timeoutId: NodeJS.Timeout | null = null;
	let hasTimedOut = false;

	const handleFailure = (
		errorType: 'timeout' | 'network',
		additionalContext?: Record<string, unknown>,
	) => {
		// Track failure for systemic detection (only on final failure, not before retry)
		let systemicResult: SystemicFailureResult | null = null;
		if (!shouldRetry || isRetry) {
			systemicResult = trackFailure(name, errorType, options.src);

			// Call custom handler if systemic failure detected
			if (systemicResult && systemicResult.isSystemic && systemicFailureCallback) {
				systemicFailureCallback(systemicResult);
			}
		}

		// If we should retry and haven't yet, schedule a retry
		if (shouldRetry && !isRetry) {
			posthog.capture('script_load_failed_will_retry', {
				script: name,
				src: options.src,
				bundleId,
				errorType,
			});

			setTimeout(() => {
				loadScript(options, name, true);
			}, retryDelay);
			return;
		}

		// Final failure - report to Sentry and PostHog
		const attemptsSuffix = isRetry ? ' after 2 attempts' : '';
		const errorName = `${name.startsWith('GTM') ? 'Google Tag Manager' : name} Load Error`;

		posthog.capture('script_load_response', {
			success: false,
			script: name,
			src: options.src,
			bundleId,
			error: new ScriptError(errorName),
			errorType,
			...(isRetry && { totalAttempts: 2 }),
			...(systemicResult && { systemic: true, probableCause: systemicResult.probableCause }),
		});

		Sentry.withScope((scope) => {
			scope.setTag('script', name);
			scope.setTag('errorType', 'loading');
			scope.setTag('bundleId', bundleId);
			scope.setTag('failureType', errorType);
			scope.setLevel('error');
			if (isRetry) {
				scope.setTag('retriedOnce', true);
			}
			if (systemicResult) {
				scope.setTag('systemicFailure', true);
			}

			Sentry.setContext('scriptDetails', {
				originalSrc: options.src,
				timeout,
				hasSuccessCallback: !!options.success,
				...(isRetry && { totalAttempts: 2 }),
				...additionalContext,
			});

			Sentry.captureException(
				new CustomError(
					`Failed to load script${attemptsSuffix}: ${name} (${errorType})${systemicResult ? ` - Part of systemic failure: ${systemicResult.probableCause}` : ''}`,
					errorName,
				),
			);
		});

		options.error?.();
	};

	// Handle module scripts differently
	if (options.type === 'module' || options.src.endsWith('.mjs')) {
		// For module scripts, we need to create the script element manually
		// since loadjs doesn't handle module scripts well
		const script = document.createElement('script');
		script.src = options.src;
		script.type = 'module';

		const handleTimeout = () => {
			hasTimedOut = true;
			script.remove();
			handleFailure('timeout', { type: 'module' });
		};

		script.onload = () => {
			if (hasTimedOut) return;
			if (timeoutId) clearTimeout(timeoutId);

			try {
				options.success?.();
				posthog.capture('script_load_response', {
					success: true,
					script: name,
					src: options.src,
					bundleId,
				});
			} catch (err) {
				posthog.capture('script_load_response', {
					success: false,
					script: name,
					src: options.src,
					bundleId,
					error: err as Error,
				});
				Sentry.withScope((scope) => {
					scope.setTag('script', name);
					scope.setTag('errorType', 'runtime-callback');
					scope.setLevel('error');
					Sentry.captureException(new ScriptError(err as Error));
				});
			}
		};

		script.onerror = () => {
			if (hasTimedOut) return;
			if (timeoutId) clearTimeout(timeoutId);
			handleFailure('network', { type: 'module' });
		};

		timeoutId = setTimeout(handleTimeout, timeout);
		document.head.appendChild(script);
		return;
	}

	// Regular scripts using loadjs
	const handleTimeout = () => {
		hasTimedOut = true;
		handleFailure('timeout');
	};

	timeoutId = setTimeout(handleTimeout, timeout);

	loadjs(options.src, bundleId, {
		before: (_path: string, scriptEl: HTMLScriptElement) => {
			if (options.mustBeFirst) {
				const container =
					document.getElementsByTagName('head')[0] || document.getElementsByTagName('body')[0];
				const firstScript = container.getElementsByTagName('script')[0];
				if (firstScript) {
					firstScript.parentNode?.insertBefore(scriptEl, firstScript);
					return false; // Prevent loadjs from inserting the script
				}
			}
			return true; // Let loadjs handle normal insertion
		},
		success: () => {
			if (hasTimedOut) return;
			if (timeoutId) clearTimeout(timeoutId);

			try {
				options.success?.();
				posthog.capture('script_load_response', {
					success: true,
					script: name,
					src: options.src,
					bundleId,
				});
			} catch (err) {
				posthog.capture('script_load_response', {
					success: false,
					script: name,
					src: options.src,
					bundleId,
					error: err as Error,
				});
				Sentry.withScope((scope) => {
					scope.setTag('script', name);
					scope.setTag('errorType', 'runtime-callback');
					scope.setLevel('error');
					Sentry.captureException(new ScriptError(err as Error));
				});
			}
		},
		error: (pathsNotFound) => {
			if (hasTimedOut) return;
			if (timeoutId) clearTimeout(timeoutId);

			let failureType: 'blocked' | 'network' | 'unknown' = 'unknown';
			let scriptInDOM = false;
			let finalUrl: string | undefined;
			let detectionError: Error | null = null;

			try {
				const result = detectLoadFailureType(options.src);
				failureType = result.type;
				finalUrl = result.finalUrl;
				scriptInDOM = failureType === 'network';
			} catch (e) {
				detectionError = e instanceof Error ? e : new Error(String(e));
				failureType = 'unknown';
			}

			// Track failure for systemic detection (only on final failure, not before retry)
			let systemicResult: SystemicFailureResult | null = null;
			if (!shouldRetry || isRetry) {
				systemicResult = trackFailure(name, failureType, options.src);

				// Call custom handler if systemic failure detected
				if (systemicResult && systemicResult.isSystemic && systemicFailureCallback) {
					systemicFailureCallback(systemicResult);
				}
			}

			// If we should retry and haven't yet, schedule a retry
			if (shouldRetry && !isRetry) {
				posthog.capture('script_load_failed_will_retry', {
					script: name,
					src: options.src,
					bundleId,
					failureType,
				});

				setTimeout(() => {
					loadScript(options, name, true);
				}, retryDelay);
				return;
			}

			// Final failure - report everything
			const attemptsSuffix = isRetry ? ' after 2 attempts' : '';
			const errorName = `${name.startsWith('GTM') ? 'Google Tag Manager' : name} Load Error`;

			posthog.capture('script_load_response', {
				success: false,
				script: name,
				src: options.src,
				bundleId,
				error: new ScriptError(errorName),
				failureType,
				...(isRetry && { totalAttempts: 2 }),
				...(systemicResult && { systemic: true, probableCause: systemicResult.probableCause }),
			});

			Sentry.withScope((scope) => {
				scope.setTag('script', name);
				scope.setTag('errorType', 'loading');
				scope.setTag('bundleId', bundleId);
				scope.setTag('failureType', failureType);
				scope.setLevel('error');
				if (isRetry) {
					scope.setTag('retriedOnce', true);
				}
				if (systemicResult) {
					scope.setTag('systemicFailure', true);
				}

				const scriptDetails = {
					originalSrc: options.src,
					finalUrl,
					failedPaths: pathsNotFound,
					mustBeFirst: !!options.mustBeFirst,
					hasSuccessCallback: !!options.success,
					failureType,
					scriptInDOM,
					...(isRetry && { totalAttempts: 2 }),
				};

				if (detectionError) {
					scope.setTag('hasDetectionError', true);
					Sentry.setContext('detectionError', {
						message: detectionError.message,
						stack: detectionError.stack,
					});
				}

				Sentry.setContext('scriptDetails', scriptDetails);
				Sentry.captureException(
					new CustomError(
						`Failed to load script${attemptsSuffix}: ${name} (${failureType}), pathsNotFound: ${pathsNotFound[0]}${detectionError ? `, detection error: ${detectionError.message}` : ''}${systemicResult ? ` - Part of systemic failure: ${systemicResult.probableCause}` : ''}`,
						errorName,
					),
				);
			});

			options.error?.();
		},
	});
};
